import { uploadLog, type LogEntry } from '@/services/logger.service'
import { getSafeErrorMessage } from '@/utils/safe-error-message'
import { getUser } from '@/features/auth'

export type LogLevel = 'error' | 'warn' | 'info' | 'debug'

type LogPayload = {
  message?: string
  error?: unknown
  category?: 'api' | 'activity' | 'client_error'
  extra?: Record<string, unknown>
}

// Check if we're in production and should send logs to server
const isProduction =
  import.meta.env.VITE_MODE === 'production' ||
  import.meta.env.VITE_MODE === 'beta' ||
  import.meta.env.VITE_MODE === 'sandbox'
const shouldUseServerLogging = isProduction

async function sendToServerLog(level: LogLevel, payload: LogPayload) {
  if (!shouldUseServerLogging) return

  try {
    const { message, error, category, extra } = payload

    const safeMessage =
      typeof message === 'string'
        ? message
        : getSafeErrorMessage(message ?? error, 'Error')
    const safeErrorMsg = error ? getSafeErrorMessage(error, '') : null

    const logEntry: LogEntry = {
      level,
      message: safeMessage || undefined,
      category,
      environment: import.meta.env.VITE_MODE,
      error: safeErrorMsg || undefined,
      route: extra?.endpoint as string | undefined,
      method: extra?.method as string | undefined,
      responseTime: extra?.responseTime as string | undefined,
      statusCode: extra?.statusCode as number | undefined,
      user: {
        dealerId: getUser()?.dealer_id || '',
        employeeId: getUser()?.employee_id || '',
      },
    }

    await uploadLog(logEntry).catch((err) => {
      // Silently fail if API is unavailable - fallback to console
      // eslint-disable-next-line no-console
      console.warn('Failed to send log to server:', err)
    })
  } catch (err) {
    // Silently fail - fallback to console logging
    // eslint-disable-next-line no-console
    console.warn('Error sending log to server:', err)
  }
}

function baseLog(level: LogLevel, payload: LogPayload) {
  const { message, error, extra } = payload
  const meta = { ...extra }
  // Ensure first arg is always a string so no object can leak into React if logging is used in UI path
  const safeMessage =
    typeof message === 'string'
      ? message
      : getSafeErrorMessage(
          message ?? error,
          level === 'error' ? 'Unhandled error' : ''
        )

  if (level === 'error') {
    // eslint-disable-next-line no-console
    console.error(safeMessage, error, meta)
  } else if (level === 'warn') {
    // eslint-disable-next-line no-console
    console.warn(safeMessage || 'Warning', meta)
  } else if (level === 'info' && !isProduction) {
    // eslint-disable-next-line no-console
    console.info(safeMessage || 'Info', meta)
  } else {
    // eslint-disable-next-line no-console
    console.debug(safeMessage || 'Debug', meta)
  }
  // Send to server logging API in production
  sendToServerLog(level, payload)
}

export const logger = {
  error: (payload: LogPayload) => baseLog('error', payload),
  warn: (payload: LogPayload) => baseLog('warn', payload),
  info: (payload: LogPayload) => baseLog('info', payload),
  debug: (payload: LogPayload) => baseLog('debug', payload),
}
